import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { 
  UserPlus, Users, Check, X, Loader2, AlertCircle, Clock, CheckCircle, XCircle
} from 'lucide-react'
import { joinRequestService, type JoinRequestWithProfile } from '@/services/joinRequestService'
import { formatDistanceToNow } from 'date-fns'

interface JoinRequestsTabProps {
  groupId: string
  onMemberAdded?: () => void
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

export function JoinRequestsTab({ groupId, onMemberAdded }: JoinRequestsTabProps) {
  const [requests, setRequests] = useState<JoinRequestWithProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const hasFetched = useRef(false)

  const fetchRequests = useCallback(async (force = false) => {
    if (hasFetched.current && !force) return
    hasFetched.current = true
    
    setLoading(true)
    setError(null)
    try {
      const data = await joinRequestService.list(groupId)
      setRequests(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests')
      hasFetched.current = false
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  const handleApprove = async (requestId: string) => {
    setProcessingId(requestId)
    try {
      await joinRequestService.approve(groupId, requestId)
      // Update local state
      setRequests(prev => 
        prev.map(r => r.requestId === requestId ? { ...r, status: 'approved' } : r)
      )
      onMemberAdded?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request')
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (requestId: string) => {
    setProcessingId(requestId)
    try {
      await joinRequestService.reject(groupId, requestId)
      // Update local state
      setRequests(prev => 
        prev.map(r => r.requestId === requestId ? { ...r, status: 'rejected' } : r)
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject request')
    } finally {
      setProcessingId(null)
    }
  }

  const pendingRequests = requests.filter(r => r.status === 'pending')
  const processedRequests = requests.filter(r => r.status !== 'pending')

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-60" />
          </CardHeader>
          <CardContent className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 text-destructive" />
        <h3 className="font-semibold mb-1">Failed to load requests</h3>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button variant="outline" onClick={() => fetchRequests(true)}>
          Try again
        </Button>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Pending Requests
            {pendingRequests.length > 0 && (
              <Badge variant="secondary">{pendingRequests.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            People who have requested to join your group
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingRequests.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">No pending requests</h3>
              <p className="text-sm text-muted-foreground">
                New join requests will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingRequests.map(request => (
                <div
                  key={request.requestId}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={request.avatar} alt={request.displayName} />
                    <AvatarFallback>{getInitials(request.displayName)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{request.displayName}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                    </p>
                    {request.message && (
                      <p className="text-sm text-muted-foreground mt-1 italic">
                        "{request.message}"
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(request.requestId)}
                      disabled={processingId === request.requestId}
                    >
                      {processingId === request.requestId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <X className="h-4 w-4 mr-1" />
                          Decline
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(request.requestId)}
                      disabled={processingId === request.requestId}
                    >
                      {processingId === request.requestId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processed Requests (collapsed by default if empty) */}
      {processedRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {processedRequests.slice(0, 10).map(request => (
                <div
                  key={request.requestId}
                  className="flex items-center gap-3 p-2 rounded text-sm"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={request.avatar} alt={request.displayName} />
                    <AvatarFallback className="text-xs">{getInitials(request.displayName)}</AvatarFallback>
                  </Avatar>
                  <span className="flex-1">{request.displayName}</span>
                  {request.status === 'approved' ? (
                    <Badge variant="secondary" className="gap-1">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      Approved
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1">
                      <XCircle className="h-3 w-3 text-muted-foreground" />
                      Declined
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
